import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { SuggestedEditKind, UUID } from '@aline/common';
import { Select } from '@aline/common';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconButton } from '@mui/material';
import { GripVertical, SquarePen, Trash2 } from 'lucide-react';
import { AlineTooltip, ConfirmationDialog } from 'src/components';
import { composeRefs } from 'src/helpers/compose-refs';
import { Flag } from 'src/helpers/feature-flags';
import { makeStyles } from 'tss-react/mui';

import { PlaybookTextarea } from './PlaybookTextarea';
import { useRuleDetailsStyles } from './rule-details-styles';
import {
  findStop,
  NEUTRAL_TONE,
  OrdinalBadge,
  SuggestedTextDiff,
  SUGGESTION_CARD_TONES,
  SUGGESTION_TONES,
  SuggestionDecision,
  SuggestionPill,
  useMoveHover,
  useStopDecision,
  useSuggestionReview,
} from './suggested-edits';
import type { PlaybookDetailRule } from './types';
import type { PositionCommitProps } from './use-rule-positions';

type Position = PlaybookDetailRule['positions'][number];

const useSuggestionStyles = makeStyles<{
  /** What tints the card: the position's own chip kind, else the kind its rule is being added or removed with. */
  cardKind: SuggestedEditKind | null;
  /** The card's own suggestion, not its move, is the current stop of the suggestions bar. */
  current: boolean;
}>()((theme, { cardKind, current }) => {
  const cardTone = cardKind ? SUGGESTION_CARD_TONES[cardKind] : null;
  /**
   * The name label follows the card's tint, so it takes the kind colour
   * whether the position or its whole rule is what is pending. On an
   * untinted card it is quiet gray; the slate block would clash on a tinted one.
   */
  const labelTone = cardKind ? SUGGESTION_TONES[cardKind] : NEUTRAL_TONE;
  /**
   * The current stop's ring: the card border one shade darker, doubled by an
   * outline in the same colour so it reads thicker without taking layout
   * space, like the ring on a category card. A move-only stop has no tint
   * and no ring; the move strip is its whole treatment.
   */
  const ringColor = current && cardTone ? cardTone.currentBorder : null;

  return {
    // A card with a suggestion is tinted whether or not it is the current stop.
    card: {
      backgroundColor: cardTone?.background,
      borderColor: ringColor ?? cardTone?.border,
      outline: ringColor ? `1px solid ${ringColor}` : undefined,
    },
    diff: {
      color: theme.appColors.iconsType,
      fontSize: 14,
      lineHeight: '22px',
    },
    // Which stance this is, in a rounded block.
    label: {
      backgroundColor: labelTone.background,
      borderRadius: 6,
      color: labelTone.color,
      flexShrink: 0,
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '20px',
      padding: theme.spacing(0.5, 1.25),
    },
    /**
     * Holds the move pill on the card's bottom row. It reaches the card's
     * edges, so the move arrow anchored to it starts at the edge, and pads the
     * pill back in a little so the pill sits inset from the card.
     */
    moveRow: {
      margin: theme.spacing(0, -2, -2),
      padding: theme.spacing(0, 0.75, 0.75),
    },
    // The decision sits at the right end of the header row.
    suggestion: {
      marginLeft: 'auto',
    },
  };
});

interface PositionFieldOrDiffProps extends PositionFieldProps {
  diffClassName: string;
  emptyLabel?: string;
  /** The text a pending patch would set, or undefined when the field has no pending patch. */
  suggestedValue?: string;
}

/** A field of a position card, or its diff while a patch is pending: read-only until resolved. */
const PositionFieldOrDiff = React.memo(function PositionFieldOrDiff({
  diffClassName,
  emptyLabel,
  suggestedValue,
  ...fieldProps
}: PositionFieldOrDiffProps): JSX.Element {
  const { classes } = useRuleDetailsStyles();

  if (suggestedValue !== undefined) {
    return (
      <div className={classes.fieldGroup}>
        <div className={classes.fieldLabel}>{fieldProps.label}</div>
        <SuggestedTextDiff
          after={suggestedValue}
          before={fieldProps.value}
          className={diffClassName}
          emptyLabel={emptyLabel}
        />
      </div>
    );
  }

  return <PositionField {...fieldProps} />;
});

interface PositionCardProps {
  index: number;
  onCommit: (positionId: UUID, props: PositionCommitProps) => void;
  onDelete: (positionId: UUID) => void;
  playbookId: string;
  position: Position;
  ruleId: string;
  /** Focus the position text on mount, for a position that was just added. */
  autoFocus?: boolean;
  /**
   * The kind the whole rule is pending as (an add or a delete), so a position
   * with no suggestion of its own still shows as part of that rule.
   */
  containerKind?: SuggestedEditKind | null;
  disabled?: boolean;
  /**
   * The card shows its text at full strength but cannot be edited, deleted
   * or dragged: its rule is pending as a whole and is decided as a whole.
   * Not `disabled`, which grays the text as if the card were switched off.
   */
  readOnly?: boolean;
}

/**
 * One stance of a rule: its requirement text, the guidance the AI comments
 * with, and the grip that drags it. Index 0 is the Starting Position; any
 * position can be deleted as long as the rule keeps one.
 *
 * A pending suggestion renders on the card: the kind chip with its ✕ / ✓
 * beside it, diffs in place of the patched fields (read-only until resolved),
 * and a full-width move pill along the bottom for a pending move.
 */
export const PositionCard = React.memo(function PositionCard({
  autoFocus,
  containerKind = null,
  disabled = false,
  index,
  onCommit,
  onDelete,
  position,
  readOnly = false,
}: PositionCardProps): JSX.Element {
  const { classes, cx } = useRuleDetailsStyles();
  const { currentStop, model } = useSuggestionReview();
  const edit = model.positionEdits.get(position.id) ?? null;
  const patch = edit?.type === 'change' ? (edit.patch ?? null) : null;
  const chipStop =
    findStop(model, position.id, 'add') ??
    findStop(model, position.id, 'delete') ??
    findStop(model, position.id, 'patch');
  const moveStop = findStop(model, position.id, 'move');
  const chipDecision = useStopDecision(chipStop);
  const moveDecision = useStopDecision(moveStop);
  const moveHover = useMoveHover(position.id);
  const isCurrent = Boolean(
    currentStop && currentStop.entityType === 'position' && currentStop.entityId === position.id
  );
  // While the move is the current stop only its pill takes the ring, not the card.
  const isMoveCurrent = Boolean(moveStop && currentStop?.key === moveStop.key);
  /**
   * Keyed off the chip's stop, not the edit: a move-only change has no chip
   * and its card stays plain, the move strip being its whole treatment.
   */
  const { classes: suggestionClasses } = useSuggestionStyles({
    cardKind: chipStop?.kind ?? containerKind,
    current: isCurrent && !isMoveCurrent,
  });
  const locked = disabled || readOnly;
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    disabled: locked,
    id: position.id,
  });
  const isStartingPosition = index === 0;
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const positionTextRef = useRef<HTMLTextAreaElement | null>(null);
  const positionLabel = isStartingPosition ? 'Starting Position' : `Fallback Position ${index}`;
  // A blank position has nothing to lose, so it goes without asking.
  const isBlank = !position.ruleText.trim() && !(position.aiGuidance ?? '').trim();
  /**
   * While a suggestion is pending the suggestion is the only thing to decide,
   * so the edit and delete hover actions stay hidden until it resolves.
   */
  const hasSuggestion = Boolean(chipStop);
  /**
   * A guidance patch is shown even while the guidance field itself is behind
   * its flag: the user has to see what they are accepting.
   */
  const suggestedGuidance =
    patch?.value.aiGuidance === undefined ? undefined : (patch.value.aiGuidance ?? '');
  const showGuidance = suggestedGuidance !== undefined || Flag.PLAYBOOK_COMMENT_GUIDANCE;

  const handleCommitRuleText = useCallback(
    (ruleText: string) => onCommit(position.id, { ruleText }),
    [onCommit, position.id]
  );
  // An empty box means no guidance, which is null rather than ''.
  const handleCommitGuidance = useCallback(
    (value: string) => onCommit(position.id, { aiGuidance: value.trim() ? value : null }),
    [onCommit, position.id]
  );

  function handleDeleteClick(): void {
    if (isBlank) onDelete(position.id);
    else setDeleteConfirmOpen(true);
  }

  function handleEditClick(): void {
    const input = positionTextRef.current;
    if (!input) return;
    input.focus();
    const end = input.value.length;
    input.setSelectionRange(end, end);
  }

  return (
    <div
      ref={setNodeRef}
      className={classes.positionRow}
      data-cy={Select.playbooks.positionCard}
      data-position-id={position.id}
      style={{
        opacity: isDragging ? 0.4 : 1,
        /**
         * Translate only. `CSS.Transform` would also apply dnd-kit's scale,
         * which is the ratio between the card's height before and after the
         * reorder — position cards differ in height by several lines, so the
         * card would visibly stretch or squash on drop.
         */
        transform: CSS.Translate.toString(transform),
        transition,
      }}
    >
      <div className={classes.gutter}>
        <OrdinalBadge current={isCurrent} index={index + 1} size="large" />
        <span className={classes.gutterRail} />
        {/* Out of flow, so leaving it out while the card cannot be dragged moves nothing. */}
        {!locked && (
          <span className={classes.dragHandle} {...listeners} {...attributes}>
            <GripVertical className={classes.dragHandleIcon} size={16} />
          </span>
        )}
      </div>
      <div className={cx(classes.card, suggestionClasses.card)}>
        {!hasSuggestion && !readOnly && (
          <div className={classes.cardActions}>
            <AlineTooltip arrow placement="top" title="Edit position">
              <IconButton
                data-cy={Select.playbooks.positionEdit}
                disabled={disabled}
                size="small"
                onClick={handleEditClick}
              >
                <SquarePen size={16} />
              </IconButton>
            </AlineTooltip>
            <AlineTooltip arrow placement="top" title="Delete position">
              {/* Wrapped so the tooltip still works while the button is disabled. */}
              <span>
                <IconButton
                  data-cy={Select.playbooks.positionDelete}
                  disabled={disabled}
                  size="small"
                  onClick={handleDeleteClick}
                >
                  <Trash2 size={16} />
                </IconButton>
              </span>
            </AlineTooltip>
          </div>
        )}
        <div className={classes.cardHeader}>
          <span className={suggestionClasses.label}>
            {isStartingPosition ? 'Starting Position' : 'Fallback Position'}
          </span>
          {chipStop && (
            <SuggestionDecision
              acceptDisabledReason={chipStop.blockedReason}
              className={suggestionClasses.suggestion}
              kind={chipStop.kind}
              rationale={chipStop.rationale}
              onAccept={chipDecision.onAccept}
              onReject={chipDecision.onReject}
              onSelect={chipDecision.onSelect}
            />
          )}
        </div>
        <PositionFieldOrDiff
          autoFocus={autoFocus}
          className={classes.field}
          dataCy={Select.playbooks.positionText}
          diffClassName={suggestionClasses.diff}
          disabled={disabled}
          label="Description"
          placeholder="Type your playbook position..."
          readOnly={readOnly}
          suggestedValue={patch?.value.ruleText}
          textareaRef={positionTextRef}
          value={position.ruleText}
          onCommit={handleCommitRuleText}
        />
        {showGuidance && (
          <PositionFieldOrDiff
            className={classes.guidanceField}
            dataCy={Select.playbooks.positionGuidance}
            diffClassName={suggestionClasses.diff}
            disabled={disabled}
            emptyLabel="No guidance"
            label="Comment Guidance"
            placeholder="Type guidance for AI to generate a comment..."
            readOnly={readOnly}
            suggestedValue={suggestedGuidance}
            value={position.aiGuidance ?? ''}
            onCommit={handleCommitGuidance}
          />
        )}
        {moveStop && (
          // The source end of the move: the arrow overlay pairs this row with the ghost.
          <div
            className={suggestionClasses.moveRow}
            data-cy={Select.playbooks.suggestionMoveStrip}
            data-move-anchor={`${position.id}:source`}
            onMouseEnter={moveHover.onMouseEnter}
            onMouseLeave={moveHover.onMouseLeave}
          >
            <SuggestionPill
              centered
              acceptDisabledReason={moveStop.blockedReason}
              acceptTitle="Accept move"
              current={isMoveCurrent}
              kind="move"
              label="AI moved"
              rationale={moveStop.rationale}
              rejectTitle="Reject move"
              onAccept={moveDecision.onAccept}
              onReject={moveDecision.onReject}
              onSelect={moveDecision.onSelect}
            />
          </div>
        )}
      </div>
      <ConfirmationDialog
        message={`Are you sure you want to delete the ${positionLabel}? This action cannot be undone.`}
        open={deleteConfirmOpen}
        primaryDataCy={Select.playbooks.positionDeleteConfirm}
        title="Delete position?"
        onClickPrimary={() => {
          setDeleteConfirmOpen(false);
          onDelete(position.id);
        }}
        onClose={() => setDeleteConfirmOpen(false)}
      />
    </div>
  );
});

interface PositionFieldProps {
  className: string;
  dataCy: string;
  label: React.ReactNode;
  onCommit: (value: string) => void;
  placeholder: string;
  value: string;
  autoFocus?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  textareaRef?: React.Ref<HTMLTextAreaElement>;
}

/** A labelled field of a position card. */
function PositionField({
  autoFocus,
  className,
  dataCy,
  disabled,
  label,
  onCommit,
  placeholder,
  readOnly,
  textareaRef,
  value,
}: PositionFieldProps): JSX.Element {
  const { classes } = useRuleDetailsStyles();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  /**
   * Focus once, when asked. An effect rather than the input's own autoFocus:
   * the card mounts off the cache write before the add mutation resolves and
   * sets the id that asks for focus, so at mount the answer is still "no".
   * The guard keeps a re-run with the same input (a tab switch) a no-op.
   */
  const hasFocusedRef = useRef(false);
  useEffect(() => {
    if (!autoFocus || hasFocusedRef.current) return;
    hasFocusedRef.current = true;
    inputRef.current?.focus();
  }, [autoFocus]);

  return (
    <div className={classes.fieldGroup}>
      <div className={classes.fieldLabel}>{label}</div>
      <PlaybookTextarea
        className={className}
        dataCy={dataCy}
        disabled={disabled}
        inputRef={composeRefs(inputRef, textareaRef)}
        placeholder={placeholder}
        readOnly={readOnly}
        value={value}
        onPersist={onCommit}
      />
    </div>
  );
}
